import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import toast from 'react-hot-toast';
import { dentalProducts } from '../resources/products/dentalProducts';

// Demo product data
const getDemoProduct = (id) => ({
  id: parseInt(id),
  name: id === "1" ? "Digital X-Ray Sensor" : "Dental Product",
  description: "High-resolution digital X-ray sensor for dental imaging with advanced features for clear and precise dental radiographs. Suitable for all dental clinics and practices.",
  long_description: "The Digital X-Ray Sensor provides exceptional image quality with 20+ line pairs per mm resolution. It features a durable design with reinforced cable connection and comes with a 3-year warranty. Compatible with most dental imaging software and includes a complete kit with positioning devices.",
  price: 3999.99,
  discount_price: 3499.99,
  category: "Equipment",
  subcategory: "Imaging",
  brand: "DentalTech",
  stock_quantity: 15,
  specifications: [
    { name: "Resolution", value: "20+ lp/mm" },
    { name: "Sensor Size", value: "Size 1 (Adult)" },
    { name: "Connection", value: "USB 3.0" },
    { name: "Warranty", value: "3 Years" },
    { name: "Software", value: "Universal Compatibility" }
  ],
  image_url: "https://via.placeholder.com/600x400?text=X-Ray+Sensor",
  additional_images: [
    "https://via.placeholder.com/600x400?text=X-Ray+Sensor+Side",
    "https://via.placeholder.com/600x400?text=X-Ray+Sensor+Back",
    "https://via.placeholder.com/600x400?text=X-Ray+Sensor+Kit"
  ],
  reviews: [
    { id: 1, user: "Dr. Smith", rating: 5, comment: "Excellent image quality and durable build.", date: "2024-05-15" },
    { id: 2, user: "Dental Clinic", rating: 4, comment: "Good product, but software could be more intuitive.", date: "2024-04-22" }
  ]
});

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        // Find product in local data first
        const foundProduct = dentalProducts.find(p => p.id.toString() === id);
        if (foundProduct) {
          setProduct(foundProduct);
        } else {
          console.log('Product not found in local data, using demo data');
          setProduct(getDemoProduct(id));
        }
      } catch (error) {
        console.log('Error loading product, using demo data');
        setProduct(getDemoProduct(id));
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast.success(`${product.name} added to cart!`);
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value > 0 && value <= (product?.stock_quantity || 20)) {
      setQuantity(value);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold text-dental-800">Product not found</h2>
        <p className="mt-4 text-gray-600">The product you're looking for doesn't exist or has been removed.</p>
        <Link to="/products" className="mt-6 inline-block bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700">
          Back to Products
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav className="mb-6">
        <ol className="flex text-sm">
          <li className="flex items-center">
            <Link to="/" className="text-gray-500 hover:text-primary-600">Home</Link>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </li>
          <li className="flex items-center">
            <Link to="/products" className="text-gray-500 hover:text-primary-600">Products</Link>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </li>
          <li className="text-dental-800 font-medium">{product.name}</li>
        </ol>
      </nav>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-6">
          {/* Product Images */}
          <div>
            <div className="bg-gray-100 rounded-lg overflow-hidden mb-4">
              <img 
                src={product.image_url || "https://via.placeholder.com/600x400?text=Dental+Product"} 
                alt={product.name}
                className="w-full h-auto object-contain"
                loading="eager"
                onError={(e) => {
                  e.target.src = "https://via.placeholder.com/600x400?text=Dental+Product";
                }}
              />
            </div>
            
            {product.additional_images && product.additional_images.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {product.additional_images.map((img, index) => (
                  <div key={index} className="bg-gray-100 rounded-lg overflow-hidden cursor-pointer">
                    <img 
                      src={img} 
                      alt={`${product.name} view ${index + 1}`}
                      className="w-full h-20 object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/600x400?text=View";
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Product Info */}
          <div>
            <span className="inline-block bg-primary-50 text-primary-700 text-xs font-medium px-2.5 py-1 rounded-full mb-2">
              {product.category}
            </span>
            {product.brand && (
              <span className="inline-block bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full mb-2 ml-2">
                {product.brand}
              </span>
            )}
            
            <h1 className="text-2xl md:text-3xl font-bold text-dental-800">{product.name}</h1>
            
            <div className="mt-4 flex items-center">
              <span className="text-2xl font-bold text-dental-800">₹{product.discount_price}</span>
              {product.discount_price < product.price && (
                <>
                  <span className="ml-3 text-lg text-gray-500 line-through">₹{product.price}</span>
                  <span className="ml-3 text-sm font-medium text-green-600">
                    {Math.round(((product.price - product.discount_price) / product.price) * 100)}% off
                  </span>
                </>
              )}
            </div>
            
            <div className="mt-6">
              <p className="text-gray-600">{product.description}</p>
            </div>
            
            {product.stock_quantity > 0 ? (
              <div className="mt-4 text-sm text-green-600 font-medium">
                In Stock ({product.stock_quantity} available)
              </div>
            ) : (
              <div className="mt-4 text-sm text-red-600 font-medium">
                Out of Stock
              </div>
            )}
            
            <div className="mt-6 flex items-center">
              <div className="mr-4">
                <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  id="quantity"
                  name="quantity"
                  min="1"
                  max={product.stock_quantity || 20}
                  value={quantity}
                  onChange={handleQuantityChange}
                  className="w-20 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              
              <button
                onClick={handleAddToCart}
                disabled={product.stock_quantity === 0}
                className={`flex-1 bg-primary-600 text-white py-3 px-6 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  product.stock_quantity === 0 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Add to Cart
              </button>
            </div>
            
            <div className="mt-8 border-t border-gray-200 pt-6">
              <div className="flex items-center text-sm text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Free shipping on orders over ₹1000
              </div>
              <div className="flex items-center text-sm text-gray-500 mt-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                100% genuine products
              </div>
              <div className="flex items-center text-sm text-gray-500 mt-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                </svg>
                Easy returns within 7 days
              </div>
            </div>
          </div>
        </div>
        
        {/* Product Tabs */}
        <div className="border-t border-gray-200">
          <div className="flex border-b border-gray-200">
            <button
              className={`py-4 px-6 text-sm font-medium ${
                activeTab === 'description' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('description')}
            >
              Description
            </button>
            <button
              className={`py-4 px-6 text-sm font-medium ${
                activeTab === 'specifications' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('specifications')}
            >
              Specifications
            </button>
            <button
              className={`py-4 px-6 text-sm font-medium ${
                activeTab === 'reviews' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('reviews')}
            >
              Reviews
            </button>
          </div>
          
          <div className="p-6">
            {activeTab === 'description' && (
              <div>
                <p className="text-gray-600">{product.long_description || product.description}</p>
              </div>
            )}
            
            {activeTab === 'specifications' && (
              <div>
                {product.specifications && product.specifications.length > 0 ? (
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                    {product.specifications.map((spec, index) => (
                      <div key={index} className="border-b border-gray-200 pb-2">
                        <dt className="text-sm font-medium text-gray-500">{spec.name}</dt>
                        <dd className="mt-1 text-sm text-dental-800">{spec.value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="text-gray-500">No specifications available for this product.</p>
                )}
              </div>
            )}
            
            {activeTab === 'reviews' && (
              <div>
                {product.reviews && product.reviews.length > 0 ? (
                  <div className="space-y-6">
                    {product.reviews.map((review) => (
                      <div key={review.id} className="border-b border-gray-200 pb-6">
                        <div className="flex items-center mb-2">
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <svg
                                key={i}
                                className={`h-5 w-5 ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                          <span className="ml-2 text-sm font-medium text-dental-800">{review.user}</span>
                          <span className="ml-auto text-sm text-gray-500">{review.date}</span>
                        </div>
                        <p className="text-gray-600">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <p className="text-gray-500">No reviews yet for this product.</p>
                    <button className="mt-4 inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700">
                      Be the first to write a review
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
